
/// <amd-module name="Build/Queue-Panel/Action" />
import { Build, BuildReason, BuildDefinition, BuildRequestValidationResult, ValidationResult } from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/Restclient/Build";
import { Action } from "Build/Flux/Action";

import { IAgentSpecification } from "DistributedTask/Common/Library/Sources/AgentSpecificationService";

import { BuildResourceParameters, ContainerResourceParameters, PackageResourceParameters, RepositoryResourceParameters, Run, RunPipelineParameters, RunResourcesParameters, Variable, PipelineResourceParameters } from "Pipelines/Client/Contracts/Pipelines";
import { IPipelinesRestClient } from "Pipelines/Client/RestClient/Pipelines";

import { IVssPageContext } from "VSS/Platform/Context";

import { MessageCardSeverity } from "VSSUI/MessageCard";
import { IServerException } from "VSS/Platform/Contribution";

export interface IEnableDiagnosticActionCreatorOptions {
    actionHub?: EnableDiagnosticActionHub;
}

export class EnableDiagnosticActionCreator {
    private _actionHub: EnableDiagnosticActionHub;

    constructor(options: IEnableDiagnosticActionCreatorOptions) {
        this._actionHub = options.actionHub || new EnableDiagnosticActionHub();
    }

    public toggleEnableDiagnostic(enabled: boolean) {
        this._actionHub.toggleEnableDiagnostic.invoke(enabled);
    }
}

export class EnableDiagnosticActionHub {
    private _toggleEnableDiagnostic: Action<boolean>;

    constructor() {
        this._toggleEnableDiagnostic = new Action<boolean>();
    }

    public get toggleEnableDiagnostic(): Action<boolean> {
        return this._toggleEnableDiagnostic;
    }
}

export interface IQueueBuildActionCreatorOptions {
    actionHub?: QueueBuildActionHub;
}

export interface IBuildQueuedPayload {
    buildId?: number;
    message?: string;
    messageSeverity?: MessageCardSeverity;
}

export class QueueBuildActionCreator {
    private _actionHub: QueueBuildActionHub;

    constructor(options: IQueueBuildActionCreatorOptions) {
        this._actionHub = options.actionHub || new QueueBuildActionHub();
    }

    public queueBuild(payload: IQueueBuildPayload) {
        if (payload.isYaml) {
            this._runPipeline(payload);
        }
        else {
            this._queueBuild(payload);
        }
    }

    private _runPipeline(payload: IQueueBuildPayload) {
        const repositoryResources = payload.repositoryResources || {};
        repositoryResources.self = {
            refName: payload.sourceBranch,
            version: payload.sourceVersion
        } as RepositoryResourceParameters;

        const resources: RunResourcesParameters = {
            repositories: repositoryResources,
            pipelines: payload.pipelineResources,
            builds: payload.buildResources,
            containers: payload.containerResources,
            packages: payload.packageResources
        } as RunResourcesParameters;

        const runPipelineParams: RunPipelineParameters = {
            stagesToSkip: payload.stagesToSkip,
            resources,
            templateParameters: payload.templateParameters,
            variables: payload.variables
        } as RunPipelineParameters;

        const pipelinesClient = payload.pageContext.getRestClient<IPipelinesRestClient>("IPipelinesRestClient");
        pipelinesClient.runPipeline(runPipelineParams, payload.projectId, payload.definitionId)
            .then((run: Run) => this._actionHub.buildQueued.invoke({ buildId: run.id }))
            .catch((ex: IServerException) => {
                this._actionHub.buildQueued.invoke({
                    buildId: undefined,
                    message: ex.message,
                    messageSeverity: MessageCardSeverity.Error
                });
            });
    }

    private _queueBuild(payload: IQueueBuildPayload) {
        const build: Build = {
            agentSpecification: payload.agentSpecification,
            queue: {
                id: payload.agentQueueId
            },
            definition: {
                id: payload.definitionId
            },
            project: {
                id: payload.projectId
            },
            sourceBranch: payload.sourceBranch,
            sourceVersion: payload.sourceVersion,
            reason: BuildReason.Manual,
            demands: payload.demands,
            parameters: payload.serializedVariables
        } as Build;

        const buildClient = payload.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
        buildClient.queueBuild(build, payload.projectId, payload.ignoreWarnings)
            .then((queuedBuild: Build) => this._actionHub.buildQueued.invoke({ buildId: queuedBuild.id }))
            .catch((validationResults: BuildRequestValidationResult[]) => {
                const errorAndWarningMessage = QueueBuildActionCreator._getErrorAndWarningMessage(validationResults);
                this._actionHub.buildQueued.invoke({
                    buildId: undefined,
                    message: errorAndWarningMessage.message,
                    messageSeverity: errorAndWarningMessage.severity
                });
            });
    }

    private static _getErrorAndWarningMessage(validationResults: BuildRequestValidationResult[]): IErrorAndWarningMessage {
        let message: string = "";
        let severity: MessageCardSeverity = MessageCardSeverity.Info;

        if (validationResults && validationResults.length > 0) {
            const errors = validationResults.filter((result: BuildRequestValidationResult) => {
                return result.result === ValidationResult.Error;
            });

            if (errors.length > 0) {
                message = this._joinValidateResults(errors);
                severity = MessageCardSeverity.Error;
            }
            else {
                message = this._joinValidateResults(validationResults);
                severity = MessageCardSeverity.Warning;
            }
        }
        // Taking into account server errors also which comes not in form of array, like no build queue permissions
        else if (validationResults) {
            return this._getErrorMessageFromServer(<any>validationResults);
        }

        return {
            message: message,
            severity: severity
        };
    }

    private static _joinValidateResults(validateResults: BuildRequestValidationResult[]): string {
        let resultMessages = validateResults.map((validationResult: BuildRequestValidationResult) => {
            return validationResult.message;
        });

        resultMessages = resultMessages.filter((message: string) => !!message);
        return resultMessages.join(",");
    }

    private static _getErrorMessageFromServer(validationResult: any): IErrorAndWarningMessage {
        let errorMessage: string = "";
        let severity: MessageCardSeverity = MessageCardSeverity.Error;

        if (validationResult) {
            errorMessage = validationResult.message || "";
        }

        if (validationResult && validationResult.serverError && validationResult.serverError.customProperties) {
            let validationResults = validationResult.serverError.customProperties.ValidationResults;
            if (validationResults && validationResults.length > 0) {
                const errors = validationResults.filter((result: BuildRequestValidationResult) => {
                    return result.result === ValidationResult.Error;
                });
                errorMessage = this._joinValidateResults(errors);

                const warnings = validationResults.filter((result: BuildRequestValidationResult) => {
                    return result.result === ValidationResult.Warning;
                });
                if (warnings && warnings.length > 0) {
                    errorMessage = this._joinValidateResults(warnings);
                    severity = MessageCardSeverity.Warning;
                }
            }
        }
        else if (validationResult && validationResult.serverError && errorMessage.length === 0) {
            errorMessage = validationResult.serverError.message || "";
        }

        return {
            message: errorMessage,
            severity: severity
        };
    }
}

export class QueueBuildActionHub {
    private _buildQueued: Action<IBuildQueuedPayload>;

    constructor() {
        this._buildQueued = new Action<IBuildQueuedPayload>();
    }

    public get buildQueued(): Action<IBuildQueuedPayload> {
        return this._buildQueued;
    }
}

export interface ISourceVersionActionCreatorOptions {
    actionHub?: SourceVersionActionHub;
}

export class SourceVersionActionCreator {
    private _actionHub: SourceVersionActionHub;

    constructor(options: ISourceVersionActionCreatorOptions) {
        this._actionHub = options.actionHub || new SourceVersionActionHub();
    }

    public updateVersion(payload: string) {
        this._actionHub.versionUpdated.invoke(payload);
    }

    public updateBranch(payload: string) {
        this._actionHub.branchUpdated.invoke(payload);
    }

    public updateTag(payload: string) {
        this._actionHub.tagUpdated.invoke(payload);
    }

    public updateShelveset(payload: string) {
        this._actionHub.shelvesetUpdated.invoke(payload);
    }
}

export class SourceVersionActionHub {
    private _versionUpdated: Action<string>;
    private _branchUpdated: Action<string>;
    private _shelvesetUpdated: Action<string>;
    private _tagUpdated: Action<string>;

    constructor() {
        this._versionUpdated = new Action<string>();
        this._branchUpdated = new Action<string>();
        this._shelvesetUpdated = new Action<string>();
        this._tagUpdated = new Action<string>();
    }

    public get versionUpdated(): Action<string> {
        return this._versionUpdated;
    }

    public get branchUpdated(): Action<string> {
        return this._branchUpdated;
    }

    public get tagUpdated(): Action<string> {
        return this._tagUpdated;
    }

    public get shelvesetUpdated(): Action<string> {
        return this._shelvesetUpdated;
    }
}

export interface IQueueBuildPayload {
    isYaml: boolean;
    agentSpecification?: IAgentSpecification;
    definitionId: number;
    enableSaveBeforeQueue?: boolean;
    definition?: BuildDefinition;
    cloneId?: number;
    cloneRevision?: number;
    saveComment?: string;
    onBuildSaved?: (buildDefinition: BuildDefinition) => void;
    agentQueueId: number;
    projectId: string;
    pageContext: IVssPageContext;
    sourceBranch?: string;
    sourceVersion?: string;
    variables?: { [key: string]: Variable };
    serializedVariables?: string;
    demands?: string[];
    ignoreWarnings: boolean;
    stagesToSkip: string[];
    repositoryResources?: { [key: string]: RepositoryResourceParameters };
    buildResources?: { [key: string]: BuildResourceParameters };
    containerResources?: { [key: string]: ContainerResourceParameters };
    packageResources?: { [key: string]: PackageResourceParameters };
    pipelineResources?: { [key: string]: PipelineResourceParameters };
    templateParameters?: { [key: string]: string }
    onSuccess?: (url: string, buildNumber: string, build: Build) => void;
}

export interface IErrorAndWarningMessage {
    message: string;
    severity: MessageCardSeverity;
}
